'use client';

import type React from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import { motion } from 'motion/react';

import { UnifiedContainer } from '@/components/layout/UnifiedContainer';
import { Accordion, AccordionContent, AccordionItem } from '@/components/ui/accordion';
import { cn } from '@/lib/utils';

interface FAQItem {
  question: string;
  answer: string;
  /** Optional content rendered below the answer (e.g. tags, CTA buttons). */
  extra?: React.ReactNode;
}

interface StandardFAQProps {
  title?: string;
  /** Small orange uppercase line above the title. */
  eyebrow?: string;
  description?: string;
  /** `primary` = main marketing section; `secondary` = supporting block (e.g. home FAQ). */
  sectionHeadingLevel?: 'primary' | 'secondary';
  faqItems: FAQItem[];
  className?: string;
  footer?: React.ReactNode;
}

/**
 * The single FAQ design used across the site: sticky slate heading on the
 * left, plain divide-y ledger rows with left chevrons on the right.
 */
export function StandardFAQ({
  title = 'Frequently Asked Questions',
  eyebrow,
  description,
  faqItems,
  sectionHeadingLevel = 'primary',
  className,
  footer,
}: StandardFAQProps) {
  const isDarkBackground =
    className?.includes('bg-oxford-blue') || className?.includes('text-white');

  return (
    <section className={cn('relative overflow-hidden bg-white py-14 sm:py-16 lg:py-24', className)}>
      <UnifiedContainer size='xl' padding='lg' className='relative z-10'>
        <div className='grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16'>
          <div className='lg:sticky lg:top-28 lg:self-start'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6 }}
            >
              {eyebrow && (
                <p
                  className={cn(
                    'mb-3 text-xs font-semibold uppercase tracking-[0.2em]',
                    isDarkBackground ? 'text-white/70' : 'text-orange-peel-600',
                  )}
                >
                  {eyebrow}
                </p>
              )}
              <h2
                className={cn(
                  'tracking-tight',
                  sectionHeadingLevel === 'primary'
                    ? 'text-pretty text-3xl font-extrabold text-slate-950 sm:text-4xl'
                    : 'text-2xl font-bold text-slate-950 md:text-3xl',
                  isDarkBackground && 'text-white',
                )}
              >
                {title}
              </h2>
              {description && (
                <p
                  className={cn(
                    'mt-4 max-w-xl text-pretty text-base leading-relaxed sm:text-lg',
                    isDarkBackground ? 'text-white/90' : 'text-slate-600',
                  )}
                >
                  {description}
                </p>
              )}
            </motion.div>
          </div>
          <div className='min-w-0'>
            <Accordion
              type='single'
              collapsible
              className={cn(
                'w-full',
                !isDarkBackground && 'divide-y divide-slate-200 border-y border-slate-200',
                isDarkBackground && 'space-y-0',
              )}
            >
              {faqItems.map((faq, index) => (
                <motion.div
                  key={faq.question}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-20px' }}
                  transition={{
                    duration: 0.35,
                    delay: Math.min(index * 0.04, 0.2),
                  }}
                >
                  <AccordionItem
                    value={`item-${index}`}
                    className={cn(
                      'transition-all duration-200',
                      isDarkBackground
                        ? 'mb-2 rounded-xl border border-white/15 bg-white/[0.07] shadow-sm backdrop-blur-sm last:mb-0 hover:border-white/25 hover:bg-white/[0.10] data-[state=open]:border-white/30 data-[state=open]:bg-white/[0.12] sm:mb-3'
                        : 'border-b-0',
                    )}
                  >
                    <AccordionPrimitive.Header className='flex'>
                      <AccordionPrimitive.Trigger
                        className={cn(
                          'flex w-full flex-1 touch-manipulation items-center gap-3 text-left transition-colors duration-200 sm:gap-4 [&>svg]:shrink-0 [&>svg]:text-current [&>svg]:transition-transform [&>svg]:duration-300 [&[data-state=open]>svg]:rotate-180',
                          isDarkBackground
                            ? 'px-4 py-3.5 text-fluid-sm font-semibold text-white hover:text-white data-[state=open]:text-white sm:px-5 sm:py-4 [&>svg]:text-white/90'
                            : 'py-5 text-lg font-bold leading-snug text-slate-950 hover:text-slate-700',
                        )}
                      >
                        <ChevronDown
                          className={cn(
                            'h-5 w-5',
                            isDarkBackground ? 'text-white/90' : 'text-orange-peel-600',
                          )}
                          aria-hidden
                        />
                        <span className='min-w-0 flex-1 leading-snug'>{faq.question}</span>
                      </AccordionPrimitive.Trigger>
                    </AccordionPrimitive.Header>

                    <AccordionContent
                      className={cn(
                        isDarkBackground
                          ? 'px-4 pb-4 pt-0 text-white sm:px-5 sm:pb-5'
                          : 'pb-6 pt-0 text-sm leading-relaxed text-slate-600',
                      )}
                    >
                      <div
                        className={cn(
                          isDarkBackground ? 'border-t border-white/10 pt-3 sm:pt-4' : 'pt-1',
                        )}
                      >
                        <p className='leading-relaxed'>{faq.answer}</p>
                        {faq.extra ? <div className='pt-3'>{faq.extra}</div> : null}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        </div>
        {footer && (
          <div className={cn('mt-8 text-center md:mt-10', isDarkBackground && 'pt-2')}>
            {footer}
          </div>
        )}
      </UnifiedContainer>
    </section>
  );
}
